<template>
	<div class="views-panel" :class="{ 'views-panel--collapsed': collapsed }">
		<!-- Header -->
		<div class="views-panel-header d-flex align-center px-3 py-2">
			<template v-if="!collapsed">
				<v-icon small class="mr-2">mdi-view-list</v-icon>
				<span class="font-weight-medium text-body-2">Viste</span>
				<v-spacer></v-spacer>
				<v-btn icon x-small @click="collapsed = true">
					<v-icon small>mdi-chevron-left</v-icon>
				</v-btn>
			</template>
			<template v-else>
				<v-btn icon x-small @click="collapsed = false">
					<v-icon small>mdi-chevron-right</v-icon>
				</v-btn>
			</template>
		</div>

		<v-divider></v-divider>

		<!-- Vista attiva: sempre visibile, indipendente dallo stato delle accordion.
		     Mostra solo il nome della vista; il percorso completo è nel tooltip. -->
		<div v-if="!collapsed && selectedView" class="views-active-view px-2 py-1">
			<v-tooltip bottom>
				<template v-slot:activator="{ on, attrs }">
					<v-chip
						small
						color="primary"
						text-color="white"
						close
						close-icon="mdi-close-circle"
						class="views-active-chip"
						v-bind="attrs"
						v-on="on"
						@click:close="clearView"
					>
						<v-icon left x-small>mdi-filter-variant</v-icon>
						<span class="text-truncate views-active-chip-label">{{ activeItemLabel }}</span>
					</v-chip>
				</template>
				<span>{{ selectedView.label }}</span>
			</v-tooltip>
			<v-divider class="mt-1"></v-divider>
		</div>

		<!-- Content (hidden when collapsed) -->
		<div v-if="!collapsed" class="views-panel-content">
			<!-- Loading state -->
			<div v-if="loading && views.length === 0" class="text-center py-4">
				<v-progress-circular indeterminate size="24" width="2" color="primary"></v-progress-circular>
			</div>

			<!-- "All" item to clear view filter -->
			<v-list dense class="py-0">
				<v-list-item
					@click="clearView"
					:class="{ 'v-list-item--active primary--text': !selectedView }"
					class="views-list-item"
				>
					<v-list-item-icon class="mr-2">
						<v-icon small>mdi-view-dashboard</v-icon>
					</v-list-item-icon>
					<v-list-item-content>
						<v-list-item-title class="text-body-2">Tutti</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
			</v-list>

			<v-divider></v-divider>

			<!-- View groups -->
			<v-list dense expand class="py-0 views-list">
				<v-list-group
					v-for="group in views"
					:key="group.key"
					:value="isGroupExpanded(group.key)"
					@input="setGroupExpanded(group.key, $event)"
					no-action
					class="views-group"
				>
					<template v-slot:activator>
						<v-list-item-icon class="mr-2">
							<v-icon small :color="isGroupActive(group.key) ? 'primary' : ''">{{ group.icon }}</v-icon>
						</v-list-item-icon>
						<v-list-item-content>
							<v-list-item-title
								class="text-body-2 font-weight-medium"
								:class="{ 'primary--text': isGroupActive(group.key) }"
							>{{ group.label }}</v-list-item-title>
						</v-list-item-content>
						<!-- Pallino a gruppo chiuso: il nome della vista è già nel chip in alto -->
						<v-list-item-action v-if="isGroupActive(group.key) && !isGroupExpanded(group.key)" class="my-0 ml-2 mr-0 views-active-dot-action">
							<v-icon x-small color="primary">mdi-circle</v-icon>
						</v-list-item-action>
					</template>

					<template v-for="item in group.items">
						<!-- Item without children -->
						<v-list-item
							v-if="!item.children || item.children.length === 0"
							:key="item.value"
							@click="selectView(group, item)"
							:class="{ 'v-list-item--active primary--text': isItemActive(group.key, item.value) }"
							class="views-list-item pl-6"
						>
							<v-list-item-content>
								<v-list-item-title class="text-body-2">{{ item.label }}</v-list-item-title>
							</v-list-item-content>
							<v-list-item-action class="my-0">
								<v-chip x-small :color="isItemActive(group.key, item.value) ? 'primary' : 'grey'" :text-color="'white'">{{ item.count }}</v-chip>
							</v-list-item-action>
						</v-list-item>

						<!-- Item with children (sub-group) -->
						<v-list-group
							v-else
							:key="item.value"
							sub-group
							no-action
							:value="isSubGroupExpanded(group.key, item.value)"
							@input="setSubGroupExpanded(group.key, item.value, $event)"
							class="views-subgroup"
						>
							<template v-slot:activator>
								<v-list-item-content @click.stop="selectView(group, item)">
									<v-list-item-title class="text-body-2 d-flex align-center">
										<span
											class="flex-grow-1"
											:class="{ 'primary--text': isItemActive(group.key, item.value) || hasActiveChild(group.key, item.value) }"
										>{{ item.label }}</span>
										<v-chip x-small :color="isItemActive(group.key, item.value) || hasActiveChild(group.key, item.value) ? 'primary' : 'grey'" :text-color="'white'" class="ml-2">{{ item.count }}</v-chip>
									</v-list-item-title>
								</v-list-item-content>
							</template>

							<v-list-item
								v-for="child in item.children"
								:key="child.value"
								@click="selectView(group, child, item)"
								:class="{ 'v-list-item--active primary--text': isItemActive(group.key, child.value, item.value) }"
								class="views-list-item"
							>
								<v-list-item-content>
									<v-list-item-title class="text-body-2">{{ child.label }}</v-list-item-title>
								</v-list-item-content>
								<v-list-item-action class="my-0">
									<v-chip x-small :color="isItemActive(group.key, child.value, item.value) ? 'primary' : 'grey'" :text-color="'white'">{{ child.count }}</v-chip>
								</v-list-item-action>
							</v-list-item>
						</v-list-group>
					</template>
				</v-list-group>
			</v-list>
		</div>

		<!-- Collapsed mode: show icons only -->
		<div v-else class="views-panel-collapsed-icons">
			<v-tooltip right v-for="group in views" :key="group.key">
				<template v-slot:activator="{ on, attrs }">
					<v-btn
						icon
						small
						v-bind="attrs"
						v-on="on"
						class="my-1"
						:color="isGroupActive(group.key) ? 'primary' : ''"
					>
						<v-icon small>{{ group.icon }}</v-icon>
					</v-btn>
				</template>
				<span>{{ group.label }}</span>
			</v-tooltip>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ViewsPanel',

	props: {
		modelDef: {
			type: String,
			required: true
		},
		advancedFilters: {
			type: Object,
			default: () => ({})
		},
		subFilters: {
			type: Object,
			default: () => ({})
		}
	},

	data() {
		return {
			views: [],
			selectedView: null, // { groupKey, itemValue, parentValue, filter, label }
			collapsed: false,
			loading: false,
			debounceTimer: null,
			expandedGroups: {},
			expandedSubGroups: {}
		}
	},

	computed: {
		// Ultimo segmento del breadcrumb ("Per Utente > Alessandro Gori" → "Alessandro Gori"):
		// è la parte significativa della vista, mostrata nel chip in alto; il percorso
		// completo resta consultabile nel tooltip.
		activeItemLabel() {
			if (!this.selectedView || !this.selectedView.label) return ''
			const parts = this.selectedView.label.split(' > ')
			return parts[parts.length - 1]
		}
	},

	watch: {
		advancedFilters: {
			handler() {
				this.debouncedFetchViews()
			},
			deep: true
		}
	},

	methods: {
		fetchViews() {
			this.loading = true

			const data = {
				advancedFilters: this.advancedFilters,
				subFilters: this.subFilters,
				filter: ''
			}

			this.axios.post(this.modelDef + '-views', data).then(response => {
				this.views = response.data.data.views || []
				this.loading = false

				// Dopo il caricamento, forza espansione del gruppo (ed eventuale sotto-gruppo) selezionato
				this.$nextTick(() => {
					if (this.selectedView) {
						this.setGroupExpanded(this.selectedView.groupKey, true)
						if (this.selectedView.parentValue !== null && this.selectedView.parentValue !== undefined) {
							this.setSubGroupExpanded(this.selectedView.groupKey, this.selectedView.parentValue, true)
						}
					}
				})
			}).catch(error => {
				console.error('Error fetching views:', error)
				this.loading = false
			})
		},

		debouncedFetchViews() {
			if (this.debounceTimer) {
				clearTimeout(this.debounceTimer)
			}
			this.debounceTimer = setTimeout(() => {
				this.fetchViews()
			}, 500)
		},

		selectView(group, item, parent) {
			const viewData = {
				groupKey: group.key,
				itemValue: item.value,
				parentValue: parent ? parent.value : null,
				filter: item.filter,
				label: parent ? `${group.label} > ${parent.label} > ${item.label}` : `${group.label} > ${item.label}`
			}

			// Toggle: if clicking the same item, deselect
			if (this.selectedView &&
				this.selectedView.groupKey === viewData.groupKey &&
				this.selectedView.itemValue === viewData.itemValue &&
				this.selectedView.parentValue === viewData.parentValue) {
				this.clearView()
				return
			}

			this.selectedView = viewData
			this.setGroupExpanded(group.key, true)
			if (parent) {
				this.setSubGroupExpanded(group.key, parent.value, true)
			}

			// Persist to localStorage
			localStorage.setItem('crud_viewFilter_' + this.modelDef, JSON.stringify(viewData))

			// Emit view filter via eventBus
			this.$eventBusUtility.$emit('viewFilters_' + this.modelDef, item.filter)
		},

		clearView() {
			// Le accordion aperte restano aperte: azzeriamo solo la selezione
			this.selectedView = null
			localStorage.removeItem('crud_viewFilter_' + this.modelDef)
			this.$eventBusUtility.$emit('viewFilters_' + this.modelDef, null)
		},

		isItemActive(groupKey, itemValue, parentValue) {
			if (!this.selectedView) return false
			return this.selectedView.groupKey === groupKey &&
				this.selectedView.itemValue === itemValue &&
				(parentValue === undefined || this.selectedView.parentValue === parentValue)
		},

		isGroupActive(groupKey) {
			return this.selectedView && this.selectedView.groupKey === groupKey
		},

		hasActiveChild(groupKey, parentValue) {
			return !!this.selectedView &&
				this.selectedView.groupKey === groupKey &&
				this.selectedView.parentValue === parentValue
		},

		isGroupExpanded(groupKey) {
			if (Object.prototype.hasOwnProperty.call(this.expandedGroups, groupKey)) {
				return this.expandedGroups[groupKey]
			}
			return this.isGroupActive(groupKey)
		},

		setGroupExpanded(groupKey, expanded) {
			if (this.expandedGroups[groupKey] === expanded) return
			this.$set(this.expandedGroups, groupKey, expanded)
			this.persistExpanded()
		},

		subGroupKey(groupKey, parentValue) {
			return groupKey + '|' + parentValue
		},

		isSubGroupExpanded(groupKey, parentValue) {
			const key = this.subGroupKey(groupKey, parentValue)
			if (Object.prototype.hasOwnProperty.call(this.expandedSubGroups, key)) {
				return this.expandedSubGroups[key]
			}
			return this.hasActiveChild(groupKey, parentValue)
		},

		setSubGroupExpanded(groupKey, parentValue, expanded) {
			const key = this.subGroupKey(groupKey, parentValue)
			if (this.expandedSubGroups[key] === expanded) return
			this.$set(this.expandedSubGroups, key, expanded)
			this.persistExpanded()
		},

		persistExpanded() {
			// Safari in navigazione privata e le quote piene fanno lanciare setItem: ricordare
			// le accordion aperte è un di più, non deve rompere il click sull'accordion.
			try {
				localStorage.setItem('crud_viewExpanded_' + this.modelDef, JSON.stringify({
					groups: this.expandedGroups,
					subGroups: this.expandedSubGroups
				}))
			} catch (e) {
				// niente persistenza: si perde solo il ripristino al cambio pagina
			}
		},

		// Il valore salvato può essere JSON valido ma di forma sbagliata (stringa, array, null):
		// senza questo expandedGroups smetterebbe di essere un oggetto e $set fallirebbe.
		asExpandedMap(value) {
			return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
		},

		restoreFromLocalStorage() {
			const saved = localStorage.getItem('crud_viewFilter_' + this.modelDef)
			if (saved) {
				try {
					this.selectedView = JSON.parse(saved)
				} catch (e) {
					localStorage.removeItem('crud_viewFilter_' + this.modelDef)
				}
			}

			const savedExpanded = localStorage.getItem('crud_viewExpanded_' + this.modelDef)
			if (savedExpanded) {
				try {
					const parsed = JSON.parse(savedExpanded)
					this.expandedGroups = this.asExpandedMap(parsed && parsed.groups)
					this.expandedSubGroups = this.asExpandedMap(parsed && parsed.subGroups)
				} catch (e) {
					localStorage.removeItem('crud_viewExpanded_' + this.modelDef)
				}
			}
		}
	},

	created() {
		this.restoreFromLocalStorage()
	},

	mounted() {
		// Debounced anche al mount: con filtri avanzati in cache il drawer
		// emette filters_ entro ~300ms e le due fetch collassano in una
		this.debouncedFetchViews()

		// Listen for filter changes to refresh counts
		this.$eventBusUtility.$on('filters_' + this.modelDef, () => {
			this.debouncedFetchViews()
		})
	},

	beforeDestroy() {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer)
		}
		this.$eventBusUtility.$off('filters_' + this.modelDef)
	}
}
</script>

<style scoped>
.views-panel {
	width: 240px;
	min-width: 240px;
	max-width: 240px;
	border-right: 1px solid rgba(0, 0, 0, 0.12);
	height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
	background-color: inherit;
	transition: width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease;
}

.views-panel--collapsed {
	width: 48px;
	min-width: 48px;
	max-width: 48px;
}

.views-panel-header {
	min-height: 40px;
}

/* Contenuto e span del chip sono flex item: senza min-width: 0 non si
   restringono e spingono la ✕ fuori dal chip. Niente overflow: hidden qui:
   la ✕ di Vuetify ha margin-right negativo e verrebbe tagliata; il
   truncate lo fa già lo span (.text-truncate). */
.views-active-chip >>> .v-chip__content {
	min-width: 0;
}

.views-active-chip-label {
	flex: 0 1 auto;
	min-width: 0;
}

.views-active-chip >>> .v-chip__close {
	flex-shrink: 0;
}

.views-active-dot-action {
	min-width: 0;
}

/* Il contenitore del caret riserva 48px: ridotto perché il pallino della
   vista attiva stia attaccato al caret, non a metà riga. Serve la catena
   completa di classi per superare la specificità della regola Vuetify. */
.views-group >>> .v-list-group__header .v-list-item__icon.v-list-group__header__append-icon {
	min-width: 24px;
}

.views-panel-content {
	overflow-y: auto;
	overflow-x: hidden;
}

.views-panel-collapsed-icons {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding-top: 8px;
}

.views-list {
	overflow-x: hidden;
}

.views-list-item {
	min-height: 32px !important;
	cursor: pointer;
}

.views-list-item:hover {
	background-color: rgba(0, 0, 0, 0.04);
}

.views-group >>> .v-list-group__header {
	min-height: 36px !important;
	padding-left: 8px !important;
}

.views-group >>> .v-list-group__items .v-list-item {
	padding-left: 24px !important;
}

.views-subgroup >>> .v-list-group__header {
	min-height: 32px !important;
	padding-left: 24px !important;
}

.views-subgroup >>> .v-list-group__items .v-list-item {
	padding-left: 36px !important;
}

/* Dark theme support */
.theme--dark .views-panel {
	border-right-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .views-list-item:hover {
	background-color: rgba(255, 255, 255, 0.04);
}

/* Mobile: hide panel */
@media (max-width: 960px) {
	.views-panel {
		display: none;
	}
}
</style>
